import { useMutation } from "@apollo/client";
import { incrementEventWebsiteView } from "@src/graphql/mutations";
import {
  IncrementEventWebsiteViewMutation,
  IncrementEventWebsiteViewMutationVariables,
} from "@src/@types/api.d";
import { useEffect, useRef } from "react";

/**
 * Custom hook to track website views for events
 *
 * This hook automatically calls the incrementEventWebsiteView mutation when a user
 * accesses the website. It prevents duplicate tracking within the same session
 * using a ref to track if the view has already been recorded.
 *
 * @param eventUuid - The UUID of the event to track views for
 * @param enabled - Whether tracking is enabled (defaults to true)
 * @returns Object containing loading state, error state, and tracking status
 */
interface UseWebsiteViewTrackingProps {
  eventUuid: string;
  enabled?: boolean;
}

export const useWebsiteViewTracking = ({
  eventUuid,
  enabled = true,
}: UseWebsiteViewTrackingProps) => {
  const [incrementView, { loading, error }] = useMutation<
    IncrementEventWebsiteViewMutation,
    IncrementEventWebsiteViewMutationVariables
  >(incrementEventWebsiteView);

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !eventUuid || hasTrackedRef.current) {
      return;
    }

    const trackView = async () => {
      try {
        await incrementView({
          variables: {
            eventId: eventUuid,
          },
        });
        hasTrackedRef.current = true;
        // console.log("Website view tracked successfully");
      } catch (err) {
        console.error("Failed to track website view:", err);
      }
    };

    // Track the view when the component mounts
    trackView();
  }, [eventUuid, enabled, incrementView]);

  return {
    loading,
    error,
    hasTracked: hasTrackedRef.current,
  };
};
